import type { MpidRequest, MpidResult, Product } from '@Visma-Real-Estate-Solutions/acquisit-ui-vue/library'
import type { Backend } from '@/lib/backend-cors/backend'
import type { BankIDDataResponse, CreateImageCollectionResponse, CreateImageResponse, ElhubConsentData, ElhubConsentResponse, OrderResponse, UIResponse } from '@/lib/backend-cors/types'
import { dataURIToBlob, generateID } from '@Visma-Real-Estate-Solutions/acquisit-ui-vue/functions'
import { useAxios } from '@/lib/backend-cors/axios'
import { Origin } from '@/stores/generic'
import { ref } from 'vue'
import type { ProtocolSubmission } from '@/stores/protocol.ts'
import type { AxiosResponse } from 'axios'
import type { Backup } from '@/stores/backup.ts'

export interface PortalBackend extends Backend {
	getPreviewTemplateByToken: (token: string) => Promise<any>
	getPreviewTemplateForHistory: (typeID: string|number, versionID: string|number, historyID: string|number) => Promise<any>
	
	set authorizationToken(value: string)
}

export const usePortal = (): PortalBackend => {
	const authorizationToken = ref<string|null>()
	
	const axios = useAxios({
		name: 'portal',
		baseURL: import.meta.env.ACQUISIT_PORTAL_URL,
		headers: () => ({
			'Authorization': 'Bearer ' + authorizationToken.value
		})
	})
	
	const getProtocolOrigin = async (...args: string[]): Promise<Origin> => {
		return Origin.UI_BUILDER
	}
	
	// Preview template functions (not part of Backend interface)
	const getPreviewTemplateByToken = async (token: string): Promise<any> => {
		const response = await axios.get('/ui-builder/v1/preview/token/' + token, {}, false)
		
		return {
			template: JSON.parse(response.data.content),
			branding: response.data.branding,
			client: response.data.client,
		}
	}
	
	const getPreviewTemplateForHistory = async (typeID: string|number, versionID: string|number, historyID: string|number): Promise<any> => {
		const response = await axios.get('/ui-builder/v1/types/' + typeID + '/versions/' + versionID + '/history/' + historyID, {}, false)
		
		return {
			template: JSON.parse(response.data.content),
			branding: response.data.branding,
			client: response.data.client,
		}
	}
	
	return {
		get label(): string {
			return axios.name
		},
		
		get baseURL(): string {
			return axios.baseURL
		},
		
		retry: axios.retry,
		
		getProtocolOrigin,
		getPreviewTemplateByToken,
		getPreviewTemplateForHistory,
		
		set authorizationToken(value: string) {
			authorizationToken.value = value
		},
		
		// Protocols
		async getOrderByTicket(ticketID: string, password: string): Promise<OrderResponse> {
			throw new Error('Not available')
		},
		
		async getOrderByToken(token: string): Promise<OrderResponse> {
			throw new Error('Not available')
		},
		
		async getUI(token: string): Promise<UIResponse> {
			throw new Error('Not available')
		},
		
		async submitProtocol(token: string, payload: Record<string, any>): Promise<AxiosResponse> {
			throw new Error('Not available')
		},
		
		async createConsent(token: string, submission: ProtocolSubmission, returnURL: string): Promise<ElhubConsentResponse> {
			return {
				type: 'signing',
				url: returnURL + '?status=success&signicatSessionId=dummy-session-portal'
			} as ElhubConsentResponse
		},
		
		async getConsentData(token: string, sessionID: string): Promise<ElhubConsentData> {
			return {
				message: 'Avtalekontroll fra Elhub er signert og godkjent, du kan gå videre til neste side.',
				protocol_signatures: [
					{
						id: 1,
						mpid: '707057500012345678',
						elhub_consent_id: 'consent-dummy-portal',
						signicat_status: 'signed',
						protocol_file_id: 1
					}
				]
			}
		},
		
		// Backups
		async getLatestBackup(token: string): Promise<Record<string, any> | null> {
			throw new Error('Not available')
		},
		
		async storeBackup(token: string, backup: Backup): Promise<void> {
			throw new Error('Not available')
		},
		
		async deleteBackup(token: string): Promise<void> {
			throw new Error('Not available')
		},
		
		// Images
		async createImageCollection(
			token: string,
			title: string | null,
			semantic: string | null,
			metadata: Record<string, any> | null
		): Promise<CreateImageCollectionResponse> {
			return {
				hash_id: generateID('image-collection-')
			}
		},
		
		async updateImageCollection(
			token: string,
			imageCollectionID: string | number,
			title: string | null,
			metadata: Record<string, any> | null
		): Promise<void> {
			// do nothing
		},
		
		async uploadImage(
			token: string,
			imageCollectionID: string | number,
			image: string,
			title: string | null,
			_onProgress?: (progress: number) => void
		): Promise<CreateImageResponse> {
			return {
				hash_id: generateID('image-'),
				url: URL.createObjectURL(dataURIToBlob(image))
			}
		},
		
		async deleteImageCollection(token: string, imageCollectionID: string | number): Promise<void> {
			// do nothing
		},
		
		async deleteImage(token: string, imageCollectionID: string | number, imageID: string | number): Promise<void> {
			// do nothing
		},
		
		// BankID
		getBankIDURL(token: string, personID: string, personNumber: string, returnURL: string): string {
			const url = new URL(window.location.href)
			url.pathname = '/bankid-dummy.html'
			url.searchParams.set('protocolUrl', returnURL)
			url.searchParams.set('personNumber', personNumber)
			url.searchParams.set('personId', personID)
			
			return url.href
		},
		
		async getBankIDData(token: string, personID: string, sessionID: string): Promise<BankIDDataResponse> {
			return {
				common_name: 'Sample Person',
				person_number: '010490-12345',
				timestamp: Math.floor((+new Date()) / 1000)
			}
		},
		
		// Products
		async getProductTerms(token: string, product: Product): Promise<string | null> {
			const productID = product.product_id ?? product.uid
			
			const response = await axios.request('get', '/order/' + token + '/products/' + productID + '/terms?ts=' + (+new Date()), {}, {}, {
				'Content-Type': 'text/plain'
			}, false)
			
			return response.data
		},
		
		async fetchMpid(token: string, request: MpidRequest): Promise<MpidResult[]> {
			return [
				{
					mpid: '707057500012345678',
					meter_number: '987654321',
					street_name: 'Demoveien',
					street_number: '5a',
					postcode: '0301',
					city: 'Oslo',
					full_address: 'Demoveien 5a'
				},
				{
					mpid: '707057500087654321',
					meter_number: '123456789',
					street_name: 'Demoveien',
					street_number: '5b',
					postcode: '0301',
					city: 'Oslo',
					full_address: 'Demoveien 5b'
				}
			]
		},
		
		// Reviews
		async submitReview(token: string, score: number, comment: string): Promise<void> {
			throw new Error('Not available')
		},
		
		parseError(error: any): any {
			return axios.parseError(error)
		},
		
		onError(error: any): any {
			return axios.onError(error)
		},
	}
}